/*
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited 2023.
 */

import {
	CHUNK_SIZE,
	GA4_EVENT_VIEW_ITEM,
	PAGE_DATA_LAYER,
	PRODUCT_DETAIL,
} from '@/data/constants/gtm';
import { getGTMProductViewEventData } from '@/data/events/data/gtm/ProductView';
import { getGTMConfig } from '@/data/events/handlers/gtm';
import { GTMProductViewPayload } from '@/data/types/GTM';
import { error as logError } from '@/data/utils/loggerUtil';
import { chunk, pickBy } from 'lodash';
import TagManager from 'react-gtm-module';
import { extractPartNumberFather } from '@/data/utils/extractPartNumberFather';

export const measure_GA4 = async (data: Awaited<ReturnType<typeof getGTMProductViewEventData>>) => {
	const dataLayerName = PAGE_DATA_LAYER;
	const { products: _products, currencyCode, marketplaceStore, value, source } = data;

	TagManager.dataLayer({ dataLayer: { ecommerce: null }, dataLayerName });

	const chunks = chunk(_products, CHUNK_SIZE);
	chunks.forEach((chunk) => {
		const products = chunk.map(
			(
				{
					name,
					id,
					price,
					brand,
					category,
					category1,
					category2,
					category3,
					category4,
					category5,
					position,
					affiliation,
					productPrice,
					inventories,
					color,
					ribbons,
					sizes,
					lowStock,
					priceType,
				},
				idx
			) => ({
				item_name: name,
				item_id: id,
				item_group_id: extractPartNumberFather(id),
				item_brand: brand,
				item_category: category,
				item_category1: category1,
				item_category2: category2,
				item_category3: category3,
				item_category4: category4,
				item_category5: category5,
				...pickBy({ price, position }, Boolean),
				affiliation,
				coupon: undefined,
				discount:
					(productPrice?.list ?? 0) > (productPrice?.offer ?? 0)
						? (productPrice?.list ?? 0) - (productPrice?.offer ?? 0)
						: 0,
				quantity: inventories?.total?.quantity,
				item_variant: '',
				hclMarketplaceSeller: affiliation,
				hclMarketplace: marketplaceStore,
				item_color: color,
				item_ribbon: ribbons,
				item_tallas_disponibles: sizes,
				item_tallas_cantidad: sizes.length.toString(),
				item_pocas_piezas: lowStock,
				item_tipo_precio: priceType,
				...(source
					? {
							index: source.index ?? idx + 1,
							item_list_id: source.item_list_id,
							item_list_name: source.item_list_name,
					  }
					: {}),
			})
		);

		const tagManagerArgs = {
			dataLayer: {
				event: GA4_EVENT_VIEW_ITEM,
				...(source
					? {
							item_list_id: source.item_list_id,
							item_list_name: source.item_list_name,
							...pickBy(
								{
									product_item_id: source.product_item_id,
									product_item_name: source.product_item_name,
									familia_producto: source.familia_producto,
								},
								Boolean
							),
					  }
					: {}),
				ecommerce: { count_view_item: 1, currency: currencyCode, value, items: products },
			},
			dataLayerName,
		};
		TagManager.dataLayer(tagManagerArgs);
	});
};

export const measure_UA = async (data: Awaited<ReturnType<typeof getGTMProductViewEventData>>) => {
	const dataLayerName = PAGE_DATA_LAYER;
	const { products: _products, currencyCode, marketplaceStore } = data;
	const chunks = chunk(_products, CHUNK_SIZE);
	chunks.forEach((chunk) => {
		const products = chunk.map(({ name, id, price, brand, category, position, affiliation }) => ({
			name,
			id,
			...pickBy({ price, brand, category, position }, Boolean),
			dimension9: affiliation,
			dimension10: marketplaceStore,
		}));
		const list = chunk[0]?.list;
		const tagManagerArgs = {
			dataLayer: {
				event: PRODUCT_DETAIL,
				ecommerce: { currencyCode, detail: { actionField: { list }, products } },
			},
			dataLayerName,
		};
		TagManager.dataLayer(tagManagerArgs);
	});
};

export const sendGTMProductViewEvent = async (payload: GTMProductViewPayload) => {
	const { settings } = payload;
	const { ga4, ua } = getGTMConfig(settings);

	if (ua || ga4) {
		const data = await getGTMProductViewEventData(payload);
		if (ua) {
			try {
				await measure_UA(data);
			} catch (error) {
				logError(undefined, 'ProductView: sendGTMProductViewEvent: measure_UA: error: %o', error);
			}
		}

		if (ga4) {
			try {
				await measure_GA4(data);
			} catch (error) {
				logError(undefined, 'ProductView: sendGTMProductViewEvent: measure_GA4: error: %o', error);
			}
		}
	}
};
